/**
 * Widget Create Modal
 * Allows creating new widgets for a dashboard
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, Database, RefreshCw, Plus } from 'lucide-react';
import { WidgetType } from '../../types';
import { endpointsApi, dashboardsApi } from '../../api/client';

interface WidgetCreateModalProps {
  dashboardId: string;
  onClose: () => void;
  onCreated: () => void;
}

const WIDGET_TYPES: { value: WidgetType; label: string; description: string }[] = [
  { value: 'table', label: 'Data Table', description: 'Displays data in table format' },
  { value: 'kpi', label: 'KPI Card', description: 'Single key performance indicator' },
  { value: 'chart_bar', label: 'Bar Chart', description: 'Compare categories' },
  { value: 'chart_line', label: 'Line Chart', description: 'Trends over time' },
  { value: 'chart_pie', label: 'Pie Chart', description: 'Show proportions' },
  { value: 'status_grid', label: 'Status Grid', description: 'Overview of multiple statuses' },
  { value: 'status_indicator', label: 'Status Indicator', description: 'Single status value' },
];

export function WidgetCreateModal({ dashboardId, onClose, onCreated }: WidgetCreateModalProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'type' | 'config'>('type');
  const [formData, setFormData] = useState({
    name: '',
    widget_type: 'table' as WidgetType,
    endpoint_id: '',
    config: {} as Record<string, unknown>,
  });

  // Fetch available endpoints
  const { data: endpoints, isLoading: endpointsLoading } = useQuery({
    queryKey: ['endpoints'],
    queryFn: endpointsApi.list,
  });

  // Generate unique grid key for react-grid-layout
  const generateGridKey = () => `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Create mutation using dashboards API
  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      widget_type: WidgetType;
      endpoint_id?: string | null;
      config?: Record<string, unknown>;
    }) => dashboardsApi.addWidget(dashboardId, {
      name: data.name,
      widget_type: data.widget_type,
      endpoint_id: data.endpoint_id || null,
      config: data.config || {},
      grid_key: generateGridKey(),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      onCreated();
      onClose();
    },
  });

  const handleSelectType = (type: WidgetType) => {
    setFormData({ ...formData, widget_type: type });
    setStep('config');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    createMutation.mutate({
      name: formData.name.trim(),
      widget_type: formData.widget_type,
      endpoint_id: formData.endpoint_id || null,
      config: formData.config,
    });
  };

  const selectedEndpoint = endpoints?.find(e => e.id === formData.endpoint_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#45bdd6]/10 rounded-lg">
              <Plus className="w-5 h-5 text-[#45bdd6]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Create New Widget
              </h2>
              <p className="text-sm text-gray-500">
                {step === 'type' ? 'Select a widget type' : 'Configure your widget'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Select Widget Type */}
        {step === 'type' && (
          <div className="flex-1 overflow-auto p-6">
            <div className="grid grid-cols-2 gap-4">
              {WIDGET_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleSelectType(type.value)}
                  className="p-4 text-left border border-gray-200 rounded-lg hover:border-[#45bdd6] hover:bg-[#45bdd6]/5 transition-colors group"
                >
                  <h3 className="font-medium text-gray-900 group-hover:text-[#45bdd6]">
                    {type.label}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {type.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Configure Widget */}
        {step === 'config' && (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Selected Type Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Typ:</span>
                <span className="px-2 py-1 bg-[#45bdd6]/10 text-[#45bdd6] rounded text-sm font-medium">
                  {WIDGET_TYPES.find(t => t.value === formData.widget_type)?.label}
                </span>
                <button
                  type="button"
                  onClick={() => setStep('type')}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Change
                </button>
              </div>

              {/* Widget Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Widget Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Production Orders"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
                  required
                  autoFocus
                />
              </div>

              {/* Endpoint Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Source (Endpoint) *
                </label>
                {endpointsLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading endpoints...
                  </div>
                ) : endpoints && endpoints.length > 0 ? (
                  <select
                    value={formData.endpoint_id}
                    onChange={(e) => setFormData({ ...formData, endpoint_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
                    required
                  >
                    <option value="">-- Select Endpoint --</option>
                    {endpoints.map((endpoint) => (
                      <option key={endpoint.id} value={endpoint.id}>
                        {endpoint.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-700 text-sm">
                      No endpoints available. Create an endpoint first under "Endpoints".
                    </p>
                  </div>
                )}

                {selectedEndpoint && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Database className="w-4 h-4" />
                      <span className="font-medium">{selectedEndpoint.name}</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">
                      {selectedEndpoint.path}
                    </p>
                  </div>
                )}
              </div>

              {/* Table-specific config */}
              {formData.widget_type === 'table' && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Table Options</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.showPagination !== false}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, showPagination: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Show pagination</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.config.striped === true}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, striped: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-[#45bdd6] focus:ring-[#45bdd6]"
                      />
                      <span className="text-sm text-gray-600">Striped rows</span>
                    </label>
                  </div>
                </div>
              )}

              {/* KPI-specific config */}
              {formData.widget_type === 'kpi' && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">KPI Options</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Aggregation</label>
                      <select
                        value={(formData.config.aggregation as string) || 'count'}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, aggregation: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      >
                        <option value="count">Count</option>
                        <option value="sum">Sum</option>
                        <option value="avg">Average</option>
                        <option value="min">Minimum</option>
                        <option value="max">Maximum</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        value={(formData.config.label as string) || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, label: e.target.value }
                        })}
                        placeholder="e.g. Orders"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setStep('type')}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
              >
                Back
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.name.trim() || !formData.endpoint_id || createMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Create Widget
                </button>
              </div>
            </div>

            {/* Error Message */}
            {createMutation.error && (
              <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Error creating widget'}
                </p>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
